import { useState, useEffect, useRef, useMemo } from "react";
import styles from "./despesas.module.css";
import ui from "../../styles/ui.module.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiTrash2, FiEdit, FiX, FiSave, FiPlus } from "react-icons/fi";
import { CgAdd, CgCloseO, CgCalculator } from "react-icons/cg";
import { LuSearch } from "react-icons/lu";
import Spinner from '../../components/Spinner/carregando';
import api, { baseURL } from "../../services/api";
import Calculadora from "../../components/Calculadora/index.jsx";
import useConfirmDialog from "../../components/ConfirmModal/useConfirmDialog.jsx";


const fmtBOB = new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' });
const getMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const toISO = (d) => d.toISOString().slice(0,10);
  return { start: toISO(start), end: toISO(end) };
};

const buildEditForm = (despesa = {}) => {
  const safe = despesa || {};
  return {
    descricao: safe.DESCRICAO || "",
    valor: safe.VALOR ? String(safe.VALOR) : "",
    moeda: safe.MOEDA || "BOB",
    dono: safe.DONO || "Pessoal",
    categoria: safe.CATEGORIA || "",
    forma_pagamento: safe.FORMA_PAGAMENTO ? String(safe.FORMA_PAGAMENTO) : "",
    data: safe.DATA_FORMATADA || "",
  };
};

const buildEmptyForm = (base) => {
  const safe = base || {};
  return {
    descricao: "",
    valor: "",
    moeda: safe.MOEDA || "BOB",
    dono: safe.DONO || "Pessoal",
    categoria: "",
    forma_pagamento: safe.FORMA_PAGAMENTO ? String(safe.FORMA_PAGAMENTO) : "",
    data: safe.DATA_FORMATADA || "",
  };
};

function Despesas() {
  const envUrl = (baseURL || "").trim();
  const apiBase = envUrl || "/api";
  const [isLoading, setIsLoading] = useState(false);
  const [taxas, setTaxas] = useState([]);
  const despesaInputRef = useRef(null);
  const loadToastShownRef = useRef(false);
  const [despesasInput, setDespesasInput] = useState(() => buildEmptyForm());
  const [categorias, setCategorias] = useState([]);
  const [novaCategoria, setNovaCategoria] = useState("");
  const [bancos, setBancos] = useState([]);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState(null);
  const [despesas, setDespesas] = useState([]);
  const [filtroDespesa, setFiltroDespesa] = useState({
    dataInicio: "",
    dataFim: "",
    dono: "",
    categoria: "",
  });

  // Estado para exibir a Calculadora do campo Valor
  const [showCalculadora, setShowCalculadora] = useState(false);
  const { confirm, ConfirmModal } = useConfirmDialog({
    confirmClassName: `${ui.butdesktop} ${ui.btnPrimary}`,
    cancelClassName: ui.butdesktop,
  });

  useEffect(() => {
    carregarDados().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Recarrega quando datas mudarem (para reduzir payload do backend)
  useEffect(() => {
    if (!filtroDespesa.dataInicio && !filtroDespesa.dataFim) return;
    carregarDados(false).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroDespesa.dataInicio, filtroDespesa.dataFim]);

  const ultimaDespesa = useMemo(() => {
    if (!despesas.length) return null;
    return despesas.reduce((maisRecente, atual) => {
      if (!maisRecente) return atual;
      const dataAtual = new Date(atual.DATA_FORMATADA || atual.DATA || 0);
      const dataMaisRecente = new Date(maisRecente.DATA_FORMATADA || maisRecente.DATA || 0);
      return dataAtual > dataMaisRecente ? atual : maisRecente;
    }, null);
  }, [despesas]);

  const closeFormulario = () => {
    setMostrarFormulario(false);
    setEditando(null);
    setDespesasInput(buildEmptyForm(ultimaDespesa));
  };

  const abrirNovoFormulario = () => {
    setEditando(null);
    setDespesasInput(buildEmptyForm(ultimaDespesa));
    setMostrarFormulario(true);
  };

  useEffect(() => {
    if (!mostrarFormulario && !editando) {
      setDespesasInput(buildEmptyForm(ultimaDespesa));
    }
  }, [ultimaDespesa, mostrarFormulario, editando]);

  // Foca o input de descrição ao abrir o formulário
  useEffect(() => {
    if (mostrarFormulario) {
      setTimeout(() => {
        try { despesaInputRef.current?.focus(); } catch {}
      }, 0);
    }
  }, [mostrarFormulario]);

  const getErrorMessage = (error) => {
    if (error.response) {
      return `Erro do servidor: ${error.response.status} - ${error.response.data.message || "Endpoint não encontrado"}`;
    } else if (error.request) {
      return "Não foi possível conectar ao servidor. Verifique se o backend está rodando.";
    } else {
      return "Ocorreu um erro inesperado. Tente novamente.";
    }
  };

  
 
  const adicionarCategoria = () => {
    const value = novaCategoria.trim();
    if (!value) return;
    if (!categorias.includes(value)) {
      setCategorias((prev) => [...prev, value]);
    }
    setNovaCategoria("");
  };

  const carregarDados = async (showToast = true) => {
    setIsLoading(true);
    try {
      const { start, end } = getMonthRange();
      const dataInicio = filtroDespesa.dataInicio || start;
      const dataFim = filtroDespesa.dataFim || end;
      const [resDespesas, resBancos, resTaxas, resCategorias] = await Promise.all([
        api.get("/despesas", { params: { dataInicio, dataFim } }),
        api.get("/bancos"),
        api.get("/consultas-saldos/taxas", { params: { dataInicio, dataFim } }),
        api.get("/despesas/categorias"),
      ]);

      const despesasApi = resDespesas.data || [];
      setDespesas(despesasApi);
      const categoriasApi = Array.isArray(resCategorias?.data) ? resCategorias.data : [];
      const categoriasUnicas = [
        ...new Set(
          [...categoriasApi, ...despesasApi.map((despesa) => despesa.CATEGORIA)]
            .filter((cat) => cat && String(cat).trim())
            .map((cat) => String(cat).trim())
        ),
      ].sort((a, b) => a.localeCompare(b));
      setCategorias(categoriasUnicas);
      setBancos(resBancos.data || []);
      setTaxas(resTaxas.data || []);

      if (!filtroDespesa.dataInicio || !filtroDespesa.dataFim) {
        setFiltroDespesa((prev) => ({
          ...prev,
          dataInicio: prev.dataInicio || dataInicio,
          dataFim: prev.dataFim || dataFim,
        }));
      }

      if (showToast && !loadToastShownRef.current) {
        toast.success("Dados carregados com sucesso!");
        loadToastShownRef.current = true;
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error(getErrorMessage(error));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  const adicionarOuAtualizarDespesa = async (e) => {
    e.preventDefault();

    const parseValorBr = (value) => {
      if (value === null || value === undefined) return NaN;
      if (typeof value === "number") return value;
      const normalized = String(value).trim().replace(/\./g, "").replace(",", ".");
      return Number(normalized);
    };

    const payload = {
      ...despesasInput,
      descricao: despesasInput.descricao.trim(),
      valor: parseValorBr(despesasInput.valor),
    };

    const resumoDespesa = [
      `Descrição: ${payload.descricao}`,
      `Valor: ${isNaN(payload.valor) ? "0" : payload.valor.toFixed(2)}`,
      `Data: ${payload.data}`,
      `Moeda: ${payload.moeda}`,
      `Dono: ${payload.dono}`,
      `Categoria: ${payload.categoria}`,
      `Forma de pagamento: ${payload.forma_pagamento || "não informada"}`,
    ].join("\n");

    const confirmacao = await confirm(
      `${editando ? "Deseja atualizar" : "Deseja adicionar"} a despesa?\n\n${resumoDespesa}`
    );

    if (!confirmacao) {
      toast.info("Operação cancelada.");
      return;
    }

    if (!payload.descricao || !payload.categoria || !payload.data || Number.isNaN(payload.valor)) {
      toast.warn("Preencha descrição, valor, categoria e data.");
      return;
    }

    try {
      setIsLoading(true);

      const mensagemSucesso = editando
        ? "Despesa atualizada com sucesso!"
        : "Despesa adicionada com sucesso!";

      if (editando) {
        await api.put(`/despesas/${editando.ID}`, payload);
      } else {
        await api.post("/despesas", payload);
      }

      await carregarDados(false);
      toast.success(mensagemSucesso);

      if (editando) {
        closeFormulario();
      } else {
        setDespesasInput(
          buildEmptyForm({
            MOEDA: payload.moeda,
            DONO: payload.dono,
            FORMA_PAGAMENTO: payload.forma_pagamento,
            DATA_FORMATADA: payload.data,
          })
        );
        setMostrarFormulario(true);
        setEditando(null);
      }
    } catch (error) {
      console.error("Erro ao salvar despesa:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  // Abrir/fechar calculadora e receber resultado para o campo Valor
  const abrirCalculadora = () => setShowCalculadora(true);
  const aoFecharCalculadora = (resultado) => {
    if (resultado !== null && resultado !== undefined) {
      setDespesasInput((prev) => ({ ...prev, valor: String(resultado) }));
    }
    setShowCalculadora(false);
  };

  const atualizarDespesa = (id) => {
    const despesaParaEditar = despesas.find((d) => d.ID === id);
    if (!despesaParaEditar) {
      toast.error("Despesa não encontrada.");
      return;
    }

    setDespesasInput(buildEditForm(despesaParaEditar));
    setEditando(despesaParaEditar);
    setMostrarFormulario(true);
  };

  const removerDespesa = async (id) => {
    const despesaParaDeletar = despesas.find((despesa) => despesa.ID === id);
    if (!despesaParaDeletar) {
      toast.error("Despesa não encontrada.");
      return;
    }

    const confirmacao = await confirm(
      `Deseja excluir a despesa "${despesaParaDeletar.DESCRICAO}" no valor de ${despesaParaDeletar.VALOR} ${despesaParaDeletar.MOEDA}?`
    );

    if (!confirmacao) {
      toast.info("Exclusão cancelada.");
      return;
    }

    try {
      setIsLoading(true);
      await api.delete(`/despesas/${id}`);
      await carregarDados(false);
      toast.success("Despesa removida com sucesso!");
    } catch (error) {
      console.error("Erro ao remover despesa:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const filtroDataDespesas = (e) => { e?.preventDefault?.(); };

  const filtrarDespesas = useMemo(() => {
    const base = Array.isArray(despesas) ? despesas : [];
    return base.filter((item) => {
      const itemDataFormatada = item?.DATA_FORMATADA || null;
      const itemCategoria = item?.CATEGORIA || "";
      const itemDono = item?.DONO || "";
      const itemDesc = (item?.DESCRICAO || '').toUpperCase();
      const dataFilInicio = filtroDespesa?.dataInicio || null;
      const dataFilFim = filtroDespesa?.dataFim || null;
      const categoriaFil = filtroDespesa?.categoria;
      const donoFil = filtroDespesa?.dono;
      const descFil = (filtroDespesa?.descricao || '').toUpperCase();

        if (!dataFilInicio && !dataFilFim && !categoriaFil && !donoFil && !descFil) return true;
        let ok = true;
        if (dataFilInicio) ok = ok && itemDataFormatada >= dataFilInicio;
        if (dataFilFim) ok = ok && itemDataFormatada <= dataFilFim;
        if (categoriaFil) ok = ok && itemCategoria === categoriaFil;
        if (donoFil) ok = ok && itemDono === donoFil;
        if (descFil) ok = ok && itemDesc.includes(descFil);
        return ok;
      }).sort((a,b) => b.DATA_FORMATADA.localeCompare(a.DATA_FORMATADA));
  }, [despesas, filtroDespesa]);

  const despesasComTaxas = useMemo(() => {
    const taxasMap = taxas.reduce((acc, taxa) => { acc[taxa.DATA] = taxa; return acc; }, {});
    return filtrarDespesas.map((d) => {
      const taxa = taxasMap[d.DATA_FORMATADA] || {};
      const valorOriginal = parseFloat(d.VALOR || 0);
      let valorBOB = valorOriginal;
      if (d.MOEDA === 'BRL') valorBOB = valorOriginal * (taxa.BOB || 0);
      if (d.MOEDA === 'USD') valorBOB = valorOriginal * (taxa.USD || 0);
      return { ...d, VALOR_CONVERTIDO_BOB: valorBOB, TAXA_USD: taxa.USD, TAXA_BOB: taxa.BOB };
    });
  }, [filtrarDespesas, taxas]);

  const despesasTotais = useMemo(() => {
    const total = despesasComTaxas.reduce((acc, item) => acc + (parseFloat(item.VALOR_CONVERTIDO_BOB) || 0), 0);
    return { BOB: total };
  }, [despesasComTaxas]);

  const total = despesasComTaxas.length;
  const exportToCsv = () => {
    const headers = ['DATA','DESCRICAO','VALOR','MOEDA','VALOR_BOB','CATEGORIA','DONO','FORMA_PAGAMENTO','BANCO'];
    const lines = [headers.join(';')].concat(
      despesasComTaxas.map(d => [
        d.DATA_FORMATADA,
        (d.DESCRICAO||'').replaceAll(';', ','),
        d.VALOR,
        d.MOEDA,
        (d.VALOR_CONVERTIDO_BOB||0).toFixed(2),
        d.CATEGORIA||'',
        d.DONO||'',
        d.FORMA_PAGAMENTO||'',
        d.NOME_BANCO||''
      ].join(';'))
    );
    const blob = new Blob(["\uFEFF" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'despesas.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Derivação de valores/totais ocorre via useMemo acima

  return (
    <main className={styles.root}>
      <ConfirmModal />
      <div className={`${styles['despesas-form-container']} ${mostrarFormulario ? styles['despesas-form-container-active'] : ''}`}>
        
          <div className={styles["despesas-form"]}>
            <button className={`${ui.butfechar}`} onClick={closeFormulario}><FiX /></button>
            <h2 className={`${ui.titg}`}>{editando ? "Editar Despesa" : "Adicionar Despesa"}</h2>
            <form className={`${ui.formgeral}`} onSubmit={adicionarOuAtualizarDespesa}>
              <div>
                <input
                  className={`${ui.inpdesktop}`}
                  type="text"
                  placeholder="Descrição da despesa"
                  value={despesasInput.descricao}
                  onChange={(e) => setDespesasInput({ ...despesasInput, descricao: e.target.value || "" })}
                  ref={despesaInputRef}
                  required
                />
              </div>
              <div className={styles["desp-valordata"]}>
                <div style={{ position: "relative" }}>
                  <input
                    className={`${ui.inpdesktop}`}
                    type="text"
                    inputMode="decimal"
                    placeholder="Valor"
                    value={despesasInput.valor}
                    onChange={(e) => setDespesasInput({ ...despesasInput, valor: e.target.value || "" })}
                    required
                  />
                  <button
                    type="button"
                    onClick={abrirCalculadora}
                    className={`${ui.calcbtn}`}
                    aria-label="Abrir calculadora para Valor"
                  >
                    <CgCalculator size={20} />
                  </button>
                </div>
                <input
                  className={`${ui.inpdesktop}`}
                  type="date"
                  placeholder="Data"
                  value={despesasInput.data}
                  onChange={(e) => setDespesasInput({ ...despesasInput, data: e.target.value || "" })}
                  required
                />
              </div> 
              <div className={styles["desp-moedadono"]}>
                <select
                  className={`${ui.seldesktop}`}
                  value={despesasInput.moeda}
                  onChange={(e) => setDespesasInput({ ...despesasInput, moeda: e.target.value || "" })}
                  aria-label="Moeda"
                  required
                >
                  <option value="USD">USD</option>
                  <option value="BRL">BRL</option>
                  <option value="BOB">BOB</option>
                </select>
                <select
                  className={`${ui.seldesktop}`}
                  value={despesasInput.dono}
                  onChange={(e) => setDespesasInput({ ...despesasInput, dono: e.target.value || "" })}
                  aria-label="Dono"
                  required
                >
                  <option value="Empresa">Empresa</option>
                  <option value="Pessoal">Pessoal</option>
                </select>
              </div>  
              <div className={styles["desp-moedadono"]}>
                <select
                  className={`${ui.seldesktop}`}
                  value={despesasInput.categoria}
                  onChange={(e) => setDespesasInput({ ...despesasInput, categoria: e.target.value || "" })}
                  aria-label="Categoria"
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  {[...categorias].sort((a, b) => a.localeCompare(b)).map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <select
                  className={`${ui.seldesktop}`}
                  value={despesasInput.forma_pagamento}
                  onChange={(e) => setDespesasInput({ ...despesasInput, forma_pagamento: e.target.value || "" })}
                  aria-label="Forma de pagamento"
                  required
                >
                  <option value="">Selecione a forma de pagamento</option>
                  {[...bancos].sort((a, b) => a.NOME.localeCompare(b.NOME)).map((banco) => (
                    <option key={banco.ID_BANCO} value={banco.ID_BANCO}>
                      {banco.NOME}
                    </option>
                  ))}
                </select>
              </div>  
              <div className={styles["desp-nova-categoria-container"]}>
                <input
                  className={`${ui.inpdesktop}`}
                  type="text"
                  placeholder="Nova categoria"
                  value={novaCategoria}
                  onChange={(e) => setNovaCategoria(e.target.value)}
                />
                <button className={`${ui.butdesktop}`} type="button" onClick={adicionarCategoria}>
                  +
                </button>
              </div>
              <div className={`${ui.divbutsalvar}`}>
                <button className={`${ui.butdesktop}`} type="submit" disabled={isLoading}>
                  {editando ? "Atualizar Despesa" : "Adicionar Despesa"}
                </button>
              </div>
              
              {/*<button className={`${ui.butdesktop}`} type="submit" disabled={isLoading}>
                {editando ? <FiSave/> : <FiSave/>}
              </button>*/}
            </form>
          </div>
          <button className={styles.fechar} onClick={closeFormulario}><FiX /></button>
        
      </div>

      <div className={styles["despesas-list-container"]}>
        <h2 className={`${ui.titg}`}>Despesas Cadastradas</h2>
        <form className={styles.filtrosDespDataDescCat} onSubmit={filtroDataDespesas}>
          <div className={styles["desp-filtro-dataini"]}>
            <label className={`${ui.labdesktop}`} htmlFor="">Data início</label>
            <label className={`${ui.labmobile}`} htmlFor="">Início</label>
            <input className={`${ui.inpdesktop}`} name='dataInicio' type="date" value={filtroDespesa.dataInicio || ''} onChange={(e)=> { setFiltroDespesa((p)=> ({...p, dataInicio: e.target.value})); } } />
          </div>
          <div className={styles["desp-filtro-datafim"]}>
            <label className={`${ui.labdesktop}`} htmlFor="">Data fim</label>
            <label className={`${ui.labmobile}`} htmlFor="">Fim</label>
            <input className={`${ui.inpdesktop}`} name='dataFim' type="date" value={filtroDespesa.dataFim || ''} onChange={(e)=> { setFiltroDespesa((p)=> ({...p, dataFim: e.target.value})); } } />
          </div>
          <div className={styles["desp-filtro-desc"]}>
            <label className={`${ui.labdesktop}`} htmlFor="filtroDescricao">Descrição</label>
            <input className={`${ui.inpdesktop}`} id="filtroDescricao" type="text" placeholder="Buscar por descrição" value={filtroDespesa.descricao || ''}
              onChange={(e)=> { setFiltroDespesa((p)=> ({...p, descricao: e.target.value})); } } />
          </div>
          <div className={styles["desp-filtro-dono"]}>
            <label className={`${ui.labdesktop}`} htmlFor="">Dono</label>
            <select className={`${ui.seldesktop}`} name='dono' value={filtroDespesa.dono || ''} onChange={(e)=> { setFiltroDespesa((p)=> ({...p, dono: e.target.value})); } }>
              <option value="">Selecione</option>
              <option value="Pessoal">Pessoal</option>
              <option value="Empresa">Empresarial</option>
            </select>
          </div>
          <div className={styles["desp-filtro-categ"]}>
            <label className={`${ui.labdesktop}`} htmlFor="">Categoria:</label>
            <select className={`${ui.seldesktop}`} name='categoria' value={filtroDespesa.categoria || ''} onChange={(e)=> { setFiltroDespesa((p)=> ({...p, categoria: e.target.value})); } }>
              <option value="">Selecione uma categoria</option>
              {categorias.map((cat, index) => (
                <option key={index} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className={"filtroBotao"}> 
            <label className={`${ui.labtransparente}`} htmlFor="" >...</label>
            <button type="submit" className={`${ui.seldesktop}`}>Filtrar</button>
            <button type="submit" className={`${ui.selmobile}`}><LuSearch/></button>
          </div>
        </form>

        {despesasComTaxas.length === 0 ? (
          <p className={styles["desp-lista-vazia"]}>Nenhuma despesa cadastrada.</p>
        ) : (
          <ul className={styles["desp-lista-despesas"]}>
            {despesasComTaxas.map((despesa) => (
              <li key={despesa.ID} className={styles["despesa-item"]}>
                <div className={styles["desp-lista-div-span"]}>
                  <span className={styles["despesa-span"]}>
                    <span className={styles["despesa-data"]}>{new Date(`${despesa?.DATA_FORMATADA}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span> <span> - </span>
                    <span className={styles["despesa-descricao"]}>{despesa?.DESCRICAO}</span>
                    <span className={styles["despesa-valor"]}>{((despesa?.VALOR) || 0).toFixed(2)} {despesa.MOEDA} {despesa?.VALOR_CONVERTIDO_BOB ? `(~ ${fmtBOB.format(despesa.VALOR_CONVERTIDO_BOB)})` : ''}</span>
                    <span className={styles["despesa-categoria"]}>{despesa?.CATEGORIA}</span>
                    <span className={styles["despesa-logonome-banco"]}>{despesa?.LOGO_BANCO && (
                      <img
                        src={`${apiBase}/logoBanco/${despesa?.LOGO_BANCO.split('/').pop()}`}
                        alt={despesa?.NOME_BANCO}
                        className={styles['desp-logo-mini']}
                      />
                    )} 
                    <span className={styles["despesa-nomebanco"]}>{despesa?.NOME_BANCO || 'Não especificado'}</span>
                    </span>
                  </span>
                </div>
                <div className={`${ui.buteditarremover}`}>
                  <button className={`${ui.buteditar}`} aria-label={`Editar despesa ${despesa.DESCRICAO}`} disabled={isLoading} onClick={() => atualizarDespesa(despesa.ID)}>
                    <FiEdit />
                  </button>
                  <button className={`${ui.butremover}`} aria-label={`Remover despesa ${despesa.DESCRICAO}`} disabled={isLoading} onClick={() => removerDespesa(despesa.ID)}>
                    <FiTrash2 />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          
        )}
        <div className={styles["desp-paginacao-simplificada"]}>
          <span>Registros: {total} • Total: {fmtBOB.format(despesasTotais.BOB || 0)}</span>
          <button className={`${ui.btnSecondary} ${styles["desp-exportar"]}`} onClick={exportToCsv}>Exportar CSV</button>
        </div>

        <div className={`${ui.floatingFab}`}>
          <button onClick={abrirNovoFormulario} className={`${ui.novaBtn} ${ui.addBtn}`} aria-label="Adicionar nova despesa">
            <FiPlus size={24} />
          </button>
        </div>
        
      </div>
      
      <ToastContainer limit={1} position="top-right" autoClose={3000} />
      {showCalculadora && (
        <div className={`${ui.divnegratelatotal}`}>
          <Calculadora onClose={aoFecharCalculadora} />
        </div>
      )}
      <Spinner
        isLoading={isLoading}
      />
    </main>
  );
}

export default Despesas;
